/**
 * @fileoverview Authentication Guard Component
 *
 * Provides route-level authentication protection by checking user authentication status.
 * Automatically redirects unauthenticated users to the login page.
 *
 * @module components/AuthGuard
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Skeleton } from '@xanectech/ui/components';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Auth - AuthGuard');

/**
 * Props for the AuthGuard component
 *
 * @interface AuthGuardProps
 */
interface AuthGuardProps {
  /** Child components to render if user is authenticated */
  children: React.ReactNode;
  /** Path to redirect unauthenticated users (default: '/auth') */
  redirectTo?: string;
}

/**
 * Authentication Guard Component
 *
 * Protects routes and components by requiring authentication before rendering.
 * Features:
 * - Checks authentication status using AuthContext
 * - Redirects to login page if user is not authenticated
 * - Preserves the original URL for post-login redirection
 * - Shows loading skeleton while checking authentication status
 * - Prevents redirect loops with internal state management
 *
 * @component
 * @param {AuthGuardProps} props - Component props
 * @returns {JSX.Element | null} Protected children or loading state
 *
 * @example
 * ```tsx
 * // Protect a route
 * <AuthGuard>
 *   <DashboardPage />
 * </AuthGuard>
 *
 * // With custom redirect path
 * <AuthGuard redirectTo="/login">
 *   <AdminPanel />
 * </AuthGuard>
 * ```
 *
 * @remarks
 * - Integrates with React Router for navigation
 * - Logs authentication checks for debugging
 * - Handles edge cases like race conditions during auth state changes
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  redirectTo = '/auth',
}) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    // Only redirect if not loading, no user, and haven't already redirected
    if (!loading && !user && !hasRedirected) {
      const currentPath = location.pathname;

      logger.info('AuthGuard: Redirecting unauthenticated user', {
        currentPath,
        user: !!user,
        loading,
      });

      const fullPath = location.pathname + location.search;

      setHasRedirected(true);
      void navigate(`${redirectTo}?redirect=${encodeURIComponent(fullPath)}`, {
        replace: true,
      });
    }
  }, [user, loading, navigate, redirectTo, location, hasRedirected]);

  // Reset redirect flag when user becomes authenticated
  useEffect(() => {
    if (user && hasRedirected) {
      setHasRedirected(false);
    }
  }, [user, hasRedirected]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        <div className='space-y-4 w-full max-w-md'>
          <Skeleton className='h-8 w-full' />
          <Skeleton className='h-8 w-3/4' />
          <Skeleton className='h-8 w-1/2' />
        </div>
      </div>
    );
  }

  // Don't render children if user is not authenticated
  if (!user) {
    return null;
  }

  // Render children if authenticated
  return <>{children}</>;
};
